import React from "react";
import { Outlet, NavLink, useNavigate } from "react-router-dom";
import { useTranslation } from "react-i18next";
import { setLang } from "@/i18n";
import { Menu, X, Ticket } from "lucide-react";
import ConciergeChat from "@/components/ConciergeChat";

const LANGS = [
  { code: "en", label: "EN" },
  { code: "fr", label: "FR" },
  { code: "pt", label: "PT" },
  { code: "ar", label: "AR" },
];

export default function Layout() {
  const { t, i18n } = useTranslation();
  const [open, setOpen] = React.useState(false);
  const navigate = useNavigate();

  const linkClass = ({ isActive }) =>
    `text-sm font-semibold tracking-wide transition-colors ${
      isActive ? "text-[color:var(--forest)]" : "text-[color:var(--ink-2)] hover:text-[color:var(--forest)]"
    }`;

  return (
    <div className="min-h-screen grain relative">
      <header className="eif-header sticky top-0 z-30">
        <div className="max-w-5xl mx-auto px-4 sm:px-6">
          <div className="h-16 flex items-center justify-between gap-4">
            <button data-testid="brand-home-link" onClick={() => navigate("/")} className="flex items-center gap-2 text-start">
              <div className="w-9 h-9 rounded-xl bg-[color:var(--forest)] flex items-center justify-center">
                <Ticket className="w-5 h-5 text-white" />
              </div>
              <div>
                <div className="font-serif-diplo text-lg leading-none">
                  <span className="text-[color:var(--forest)]">EVENT</span>{" "}
                  <span className="text-[color:var(--amber)]">HUB</span>
                </div>
                <div className="text-[10px] font-mono-diplo text-[color:var(--ink-3)] mt-0.5 tracking-widest uppercase">
                  Discover · Reserve · Attend
                </div>
              </div>
            </button>

            <nav className="hidden md:flex items-center gap-5">
              <NavLink to="/" end className={linkClass} data-testid="nav-browse">{t("nav.browse")}</NavLink>
              <NavLink to="/my-bookings" className={linkClass} data-testid="nav-my-bookings">{t("nav.myBookings")}</NavLink>
              <NavLink to="/media" className={linkClass} data-testid="nav-media">{t("nav.media")}</NavLink>
              <NavLink to="/scanner" className={linkClass} data-testid="nav-scanner">{t("nav.scanner")}</NavLink>
              <NavLink to="/admin" className={linkClass} data-testid="nav-admin">{t("nav.admin")}</NavLink>
            </nav>

            <div className="hidden md:flex items-center gap-1">
              {LANGS.map((l) => (
                <button
                  key={l.code}
                  data-testid={`lang-${l.code}`}
                  data-active={i18n.language === l.code}
                  className="lang-pill"
                  style={{ borderColor: "var(--line-strong)", color: "var(--ink-2)" }}
                  onClick={() => setLang(l.code)}
                >
                  {l.label}
                </button>
              ))}
            </div>

            <button
              className="md:hidden p-2 text-[color:var(--ink)]"
              onClick={() => setOpen(!open)}
              data-testid="mobile-menu-toggle"
              aria-label="Menu"
            >
              {open ? <X className="w-5 h-5" /> : <Menu className="w-5 h-5" />}
            </button>
          </div>

          {open && (
            <div className="md:hidden pb-4 border-t border-[color:var(--line)] pt-3 space-y-1 animate-fade-up">
              <NavLink to="/" end className="block py-2 text-sm font-semibold" onClick={() => setOpen(false)} data-testid="mnav-browse">{t("nav.browse")}</NavLink>
              <NavLink to="/my-bookings" className="block py-2 text-sm font-semibold" onClick={() => setOpen(false)} data-testid="mnav-my-bookings">{t("nav.myBookings")}</NavLink>
              <NavLink to="/media" className="block py-2 text-sm font-semibold" onClick={() => setOpen(false)} data-testid="mnav-media">{t("nav.media")}</NavLink>
              <NavLink to="/scanner" className="block py-2 text-sm font-semibold" onClick={() => setOpen(false)} data-testid="mnav-scanner">{t("nav.scanner")}</NavLink>
              <NavLink to="/admin" className="block py-2 text-sm font-semibold" onClick={() => setOpen(false)} data-testid="mnav-admin">{t("nav.admin")}</NavLink>
              <div className="flex items-center gap-1 pt-2">
                {LANGS.map((l) => (
                  <button
                    key={l.code}
                    data-testid={`m-lang-${l.code}`}
                    data-active={i18n.language === l.code}
                    className="lang-pill"
                    style={{ borderColor: "var(--line-strong)", color: "var(--ink-2)" }}
                    onClick={() => { setLang(l.code); setOpen(false); }}
                  >
                    {l.label}
                  </button>
                ))}
              </div>
            </div>
          )}
        </div>
      </header>

      <main className="relative z-10">
        <Outlet />
      </main>

      <ConciergeChat />

      <footer className="mt-16 pb-6 text-center text-[10px] font-mono uppercase tracking-widest text-[color:var(--ink-3)]">
        <a
          data-testid="footer-agency-link"
          href="https://pakebbagaye.com.free/"
          target="_blank"
          rel="noreferrer"
          className="inline-flex items-center gap-1 hover:text-[color:var(--forest)] transition-colors"
        >
          Crafted by <span className="font-bold text-[color:var(--forest)]">Nexora Digital</span>
        </a>
      </footer>
    </div>
  );
}
